import menu from "./menu.json";
import { faq } from "./faq";

/**
 * One searchable destination on the site.
 *
 * The index is curated by hand rather than extracted from the templates,
 * because roughly 40% of `$t()` call sites pass a *dynamic* key
 * (`$t(trail.header)`, `$t(item.key)`, `$t(service.title)`), which no static
 * extractor can follow. Entries store i18n *keys*, never text — the text is
 * resolved at runtime, so all five locales are searchable with one index.
 */
export interface SearchEntry {
    /** i18n key for the result heading. */
    titleKey: string;
    /** i18n keys whose text is matched but not shown as the heading. */
    bodyKeys: string[];
    /**
     * Optional i18n key holding a space-separated list of synonyms, matched but
     * never displayed. It exists because search can only find words the copy
     * actually uses: the pricing section says "cost" in English and "κόστος" in
     * Greek, so without this a search for "price" or "τιμή" finds nothing.
     * Translated like any other string — one list per locale.
     */
    aliasKey?: string;
    /**
     * Set when the title is a sentence rather than a label — the FAQ questions
     * are the only ones today. A word inside "Is the refuge accessible for
     * guests with limited mobility?" is far weaker evidence than the same word
     * inside "Booking", so a prose title is scored as body text; see
     * `rankSearchResults()`.
     */
    proseTitle?: boolean;
    /** Route path, without a locale prefix — `localePath()` adds that. */
    path: string;
    /** Optional `id` of the section to scroll to. Must exist in the page. */
    hash?: string;
    /** i18n key for the section this result sits under, shown as a breadcrumb. */
    groupKey: string;
    /** Phosphor icon name. */
    icon: string;
}

interface MenuItem {
    key: string;
}

interface MenuCategory {
    id: string;
    icon: string;
    labelKey: string;
    items: MenuItem[];
}

/**
 * Menu categories come straight from `data/menu.json`, so adding a dish makes
 * it searchable without touching this file. Results link to the menu page,
 * which has its own in-page filter for narrowing further.
 */
const menuEntries: SearchEntry[] = (menu as MenuCategory[]).map(category => ({
    titleKey: category.labelKey,
    bodyKeys: category.items.map(item => item.key),
    path: "/services/menu",
    groupKey: "Food & Drink",
    icon: category.icon,
}));

const pageEntries: SearchEntry[] = [
    // ── Home ──────────────────────────────────────────────────────
    {
        titleKey: "Home",
        bodyKeys: ["welcomeMessage", "refugeSubtitle", "launchPageHeader", "heartOfTzoumerka",
            "sectionText1", "sectionText2", "sectionText3", "informationText"],
        path: "/",
        hash: "discover",
        groupKey: "Home",
        icon: "ph:house",
    },

    // ── The Refuge ────────────────────────────────────────────────
    {
        titleKey: "The Refuge",
        bodyKeys: ["refugeSubtitle", "Refuge Full Name", "Tzoumerka"],
        path: "/refuge",
        groupKey: "The Refuge",
        icon: "ph:mountains",
    },
    {
        titleKey: "Our Story",
        bodyKeys: ["storySubtitle", "storySplit", "builtIn", "renovatedIn"],
        path: "/refuge/story",
        groupKey: "The Refuge",
        icon: "ph:book-open",
    },
    {
        titleKey: "howToGetHere",
        aliasKey: "searchAliasDirections",
        bodyKeys: ["approachSubtitle", "approach1", "approach2", "approach3",
            "possibleWays", "methodsOfTravel", "directionsTag", "transportTag"],
        path: "/refuge/approach",
        groupKey: "The Refuge",
        icon: "ph:map-trifold",
    },
    {
        titleKey: "byCar",
        bodyKeys: ["fromArta", "fromAthens", "fromIoannina"],
        path: "/refuge/approach",
        hash: "by-car",
        groupKey: "How to get here",
        icon: "ph:car",
    },
    {
        titleKey: "byBus",
        bodyKeys: ["ktelTzoumerka"],
        path: "/refuge/approach",
        hash: "by-bus",
        groupKey: "How to get here",
        icon: "ph:bus",
    },
    {
        titleKey: "byWalking",
        bodyKeys: ["fromKatarraktis"],
        path: "/refuge/approach",
        hash: "by-walking",
        groupKey: "How to get here",
        icon: "ph:person-simple-walk",
    },

    // ── Booking ───────────────────────────────────────────────────
    {
        titleKey: "Booking",
        aliasKey: "searchAliasBooking",
        bodyKeys: ["bookingSubtitle", "howToBook", "bookingStrategy", "bookingInformation",
            "contact", "callNow", "quickHighlights"],
        path: "/refuge/booking",
        hash: "booking-contact",
        groupKey: "The Refuge",
        icon: "ph:calendar-check",
    },
    {
        titleKey: "seasons",
        bodyKeys: ["seasonExplanation", "mainSeason", "winter", "juneOctober", "novemberMay",
            "openEveryDay", "onlyWeekends", "highlightSeasonTitle", "highlightSeasonValue"],
        path: "/refuge/booking",
        hash: "seasons",
        groupKey: "Booking",
        icon: "ph:sun-horizon",
    },
    {
        titleKey: "overnightPricing",
        aliasKey: "searchAliasPricing",
        bodyKeys: ["sleepCosts", "accommodationPrice", "tentCost", "payment", "paymentInfo",
            "AccommodationWithFood", "Camping", "highlightAccommodationTitle",
            "highlightAccommodationValue", "highlightTentTitle", "highlightTentValue",
            "highlightCapacityTitle", "highlightCapacityValue"],
        path: "/refuge/booking",
        hash: "pricing",
        groupKey: "Booking",
        icon: "ph:currency-eur",
    },
    {
        titleKey: "refunds",
        bodyKeys: ["refundInfo1", "refundInfo2", "bookingDisclaimer",
            "highlightCancellationTitle", "highlightCancellationValue"],
        path: "/refuge/booking",
        hash: "refunds",
        groupKey: "Booking",
        icon: "ph:arrow-counter-clockwise",
    },

    // ── FAQ ───────────────────────────────────────────────────────
    // The page itself, for someone looking for the FAQ rather than for an
    // answer. The questions carry their own entries below, so this one holds
    // only the page's own words - otherwise every question would match twice.
    {
        titleKey: "faqTitle",
        bodyKeys: ["faqSubtitle", "faqMoreQuestions"],
        path: "/refuge/faq",
        groupKey: "The Refuge",
        icon: "ph:question",
    },

    // ── Media ─────────────────────────────────────────────────────
    {
        titleKey: "Photos",
        bodyKeys: ["photoSubtitle"],
        path: "/refuge/photos",
        groupKey: "The Refuge",
        icon: "ph:image",
    },
    {
        titleKey: "Videos",
        bodyKeys: ["videoSubtitle", "videoTitlePromotion", "videoTitleDrone", "videoTitleTimelapse"],
        path: "/refuge/videos",
        groupKey: "The Refuge",
        icon: "ph:video",
    },
    {
        titleKey: "liveWebCams",
        aliasKey: "searchAliasCameras",
        bodyKeys: ["camerasSubtitle", "webCamInfo", "wonderfulViews", "webCams", "camera1"],
        path: "/refuge/cameras",
        groupKey: "The Refuge",
        icon: "ph:webcam",
    },

    // ── Services ──────────────────────────────────────────────────
    {
        titleKey: "Services",
        bodyKeys: ["servicesSubtitle", "allOurServices", "accommodationIndexDescription",
            "menuIndexDescription"],
        path: "/services",
        groupKey: "Services",
        icon: "ph:concierge-bell",
    },
    {
        titleKey: "sleepingAccommodation",
        aliasKey: "searchAliasSleeping",
        bodyKeys: ["Overnight Stay", "overnightHeader", "overnightPricing", "refugeCapacity",
            "visitorsNeedToHave", "sleepingBag", "personalSheets", "pillowCase", "toiletries",
            "flashLight", "byLawSleepingUtilitiesNotRequired", "forMoreInfoCheckFEK",
            "capacityTag", "essentialsTag", "rulesTag"],
        path: "/services/accommodation",
        hash: "sleeping",
        groupKey: "Accommodation",
        icon: "ph:bed",
    },
    {
        titleKey: "Camping",
        bodyKeys: ["campingToSplit", "tentCost", "adventureTag"],
        path: "/services/accommodation",
        hash: "camping",
        groupKey: "Accommodation",
        icon: "ph:tent",
    },
    {
        titleKey: "Common Area",
        bodyKeys: ["commonAreaToSplit", "amenitiesTag"],
        path: "/services/accommodation",
        hash: "common-area",
        groupKey: "Accommodation",
        icon: "ph:armchair",
    },
    {
        titleKey: "Kitchen",
        bodyKeys: ["kitchenToSplit"],
        path: "/services/accommodation",
        hash: "kitchen",
        groupKey: "Accommodation",
        icon: "ph:cooking-pot",
    },
    {
        titleKey: "ourMenu",
        aliasKey: "searchAliasFood",
        bodyKeys: ["menuSubtitle", "Food & Drink", "Food & Drinks", "menuOvernightNotice",
            "menuOvernightPrice"],
        path: "/services/menu",
        groupKey: "Services",
        icon: "ph:fork-knife",
    },

    // ── Activities ────────────────────────────────────────────────
    {
        titleKey: "Activities",
        bodyKeys: ["activitiesYouCanDo", "hikingTrailsDescription", "alpineActivitiesDescription"],
        path: "/activities",
        groupKey: "Activities",
        icon: "ph:compass",
    },
    {
        titleKey: "Alpine",
        bodyKeys: ["winterActivities1", "winterActivities2", "extremeTag", "altSkier"],
        path: "/activities/alpine",
        groupKey: "Activities",
        icon: "ph:snowflake",
    },
    {
        titleKey: "Winter School",
        bodyKeys: ["winterSchool1", "winterSchool2", "trainingTag"],
        path: "/activities/alpine",
        hash: "winter-school",
        groupKey: "Alpine",
        icon: "ph:student",
    },
    {
        titleKey: "Ice Climbing",
        bodyKeys: ["iceClimbingToSplit", "iceClimbingQuote", "enjoyTheoharopoulos"],
        path: "/activities/alpine",
        hash: "ice-climbing",
        groupKey: "Alpine",
        icon: "ph:mountains",
    },
    {
        titleKey: "Alpine Skiing",
        bodyKeys: ["alpineSkiing1", "alpineSkiing2", "alpineSkiing3", "alpineSkiing4"],
        path: "/activities/alpine",
        hash: "skiing",
        groupKey: "Alpine",
        icon: "ph:person-simple-ski",
    },
    {
        titleKey: "Hiking",
        bodyKeys: ["hikingHeader", "hikingParagraph", "Hiking Trails", "hikingTrails",
            "readyToHike", "exploreTrailsDescription"],
        path: "/activities/hiking-trails",
        groupKey: "Activities",
        icon: "ph:sneaker",
    },
];

/**
 * Individual trails. The ids mirror `trails[].id` in
 * `pages/activities/hiking-trails.vue`, which renders `id="trail-N"` anchors.
 */
const trailEntries: SearchEntry[] = [
    { titleKey: "anavasiMapheader", bodyKeys: ["anavasiMapDetails", "difficultyModerate"], hash: "trail-0" },
    { titleKey: "katarraktisKatafidiHeader", bodyKeys: ["katarraktisKatafidiDetails", "difficultyChallenging"], hash: "trail-1" },
    { titleKey: "katarraktisMpresenaKatafidiHeader", bodyKeys: ["katarraktisMpresenaKatafidiDetails", "difficultyChallenging"], hash: "trail-2" },
    { titleKey: "katarraktisAgiosKosmasHeader", bodyKeys: ["katarraktisAgiosKosmasDetails", "difficultyEasy"], hash: "trail-3" },
    { titleKey: "katafygioKatarraktesHeader", bodyKeys: ["katafygioKatarraktesDetails", "difficultyModerate"], hash: "trail-4" },
    { titleKey: "katarraktesKatafygioHeader", bodyKeys: ["katarraktesKatafygioDetails", "difficultyModerate"], hash: "trail-5" },
].map(trail => ({
    ...trail,
    path: "/activities/hiking-trails",
    groupKey: "Hiking Trails",
    icon: "ph:path",
}));

/**
 * One entry per answer, deep-linked to the anchor it renders with, so a search
 * for "wifi" lands on the wifi answer rather than the top of a page of
 * eighteen. Generated from `data/faq.ts`: adding a question makes it
 * searchable with no edit here, the way a menu item already does.
 *
 * The question is the title because it is what a visitor recognises - "Are
 * dogs allowed?" reads as an answer to their own words in a way "FAQ" does
 * not - and the group heading is the breadcrumb.
 */
const faqEntries: SearchEntry[] = faq.map(entry => ({
    titleKey: entry.questionKey,
    bodyKeys: [entry.answerKey],
    proseTitle: true,
    path: "/refuge/faq",
    hash: entry.id,
    groupKey: entry.groupKey,
    icon: entry.icon,
}));

export const searchIndex: SearchEntry[] = [...pageEntries, ...faqEntries, ...trailEntries, ...menuEntries];
